import type { ErrorComponentProps } from '@tanstack/react-router';
import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Alert, Button, Container, Group, Stack, Title } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useEffect } from 'react';
import { BaseWebAppLayout } from '@app/layout/components/BaseWebAppLayout.tsx';
import { NotFound } from '@components/NotFound';
import { NotLoggedIn } from '@components/NotLoggedIn';
import { AppRouterContext } from '@app/router';
import { CookieConsentBanner } from '@features/analytics/components/CookiesConsentBanner.tsx';
import { useClarity } from '@features/analytics/hooks/useClarity';
import { trackException } from '@lib/app-insights/appInsights';

export const Route = createRootRouteWithContext<AppRouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootErrorComponent({error}: ErrorComponentProps) {
  const router = useRouter();
  const queryErrorResetBoundary = useQueryErrorResetBoundary();

  useEffect(() => {
    trackException(error);
  }, [error]);

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg" align="center">
        <Title order={2}>Something went wrong</Title>
        <Alert
          icon={<IconInfoCircle size={16}/>}
          title="Unexpected Error"
          color="red"
          variant="light"
          w="100%"
        >
          {error.message || 'An unexpected error occurred. Please try again.'}
          <Group mt="md">
            <Button
              variant="light"
              onClick={() => router.invalidate()}
            >
              Retry
            </Button>
            <Button variant="outline" onClick={() => router.navigate({to: '/'})}>
              Return Home
            </Button>
          </Group>
        </Alert>
      </Stack>
    </Container>
  );
}

function RootLayout() {
  const {auth} = Route.useRouteContext();
  useClarity();

  // Show login screen if not authenticated
  if (!auth.isAuthenticated) {
    return (
      <>
        <NotLoggedIn/>
        <TanStackRouterDevtools/>
      </>
    );
  }

  return (
    <>
      <BaseWebAppLayout>
        <Outlet/>
      </BaseWebAppLayout>
      <CookieConsentBanner privacyPolicyUrl="/dpa/"/>
      <TanStackRouterDevtools/>
    </>
  );
}
